/**
 * Record what a scheduled-drop edit overwrote, so it can be undone.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The prior values exist ONLY here. The drop lives in SMSWiser, whose read
 * mapping discards the campaign and route ids a revert has to send back, and
 * re-reading each drop would cost an API call per row against an endpoint with
 * no 429 retry. The list the user is looking at already holds them.
 *
 * Called AFTER the edit lands, and only for the drops that actually changed —
 * offering to undo an edit that failed would be worse than offering nothing.
 * Never throws: losing the safety net must not fail the edit that succeeded.
 */

import {
  toClickIdStructure,
  toDomainScheme,
  type ShortlinkClickIdStructure,
  type ShortlinkDomainScheme,
} from '@trafficpulse/shared';

import api from '@/services/api';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

/** The edit payload shape — an undo re-sends exactly this. */
export interface RemoteEditPrior {
  dropId: number;
  uniqueId?: string;
  title?: string;
  senderName?: string;
  smsText?: string[];
  campaign?: string;
  smsGatewayRoute?: string;
  shortLinkTld?: string;
  forceSend?: boolean;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
}

/** The values a drop holds right now, in the shape the edit endpoint takes. */
export function priorOf(drop: PreparedDrop): RemoteEditPrior | null {
  if (drop.sentDropId == null) return null;
  return {
    dropId: drop.sentDropId,
    uniqueId: drop.sentDropUniqueId ?? undefined,
    title: drop.sentTitle ?? drop.titleBase,
    senderName: (drop.senderNames ?? []).join(', '),
    smsText: drop.smsTexts ?? [],
    campaign: String(drop.wiserId),
    smsGatewayRoute: String(drop.routeId),
    shortLinkTld: drop.shortLinkTldValue,
    forceSend: drop.forceSend ?? undefined,
    // A row synced before these existed was sent with SMSWiser's defaults.
    clickIdStructure: toClickIdStructure(drop.clickIdStructure),
    domainScheme: toDomainScheme(drop.domainScheme),
  };
}

export async function recordRemoteEdit(input: {
  countryKey: string;
  priors: RemoteEditPrior[];
  undoGroupId?: string;
}): Promise<void> {
  if (input.priors.length === 0) return;
  try {
    await api.post('/smswiser/prepared-drops/undo/remote-edit', input);
  } catch {
    // The edit already succeeded; an unrecorded undo is a missing safety net,
    // not a failure worth surfacing over it.
  }
}
